<template>
  <div>
    <v-layout>
      <v-col cols="3">
        <filter-comp
          :accounts="support_accounts"
          :hide_active_filter="true"
          :hide_sort_filter="true"
          :show_named_select_filter="true"
          view="select"
          @apply_filters="
            applyFilters(
              $event.start_date,
              $event.end_date,
              $event.filters,
              $event.named_only
            )
          "
        ></filter-comp>
      </v-col>
      <v-col>
        <div v-if="selects">
          <v-data-table
            :headers="headers"
            :items="selects"
            item-key="id"
            class="elevation-1 row-pointer"
            :loading="loading"
            hide-default-footer
            @click:row="viewSelect($event.query_id)"
          >
            <template v-slot:[`item.account_name`]="{ item }">
              {{ getAccountDetails(item.account_id).name }}
            </template>

            <template v-slot:[`item.user_name`]="{ item }">
              {{ getUserDetails(item.account_id, item.user_id).name }}
            </template>

            <template v-slot:[`item.query_name_id`]="{ item }">
              {{ item.query_name || item.query_id }}
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <select-actions
                :select="item"
                show_users
                show_details
                show_usage
                show_orders
              ></select-actions>
            </template>

            <template v-slot:footer>
              <div><v-divider></v-divider></div>
              <v-layout justify-end>
                <paging-comp
                  :paging_id="getPagingId()"
                  :has_more="has_more"
                  @next_page="$emit('next_page', { last_id: $event.last_id })"
                  @prev_page="$emit('prev_page', { first_id: $event.first_id })"
                  ref="pagingComp"
                ></paging-comp>
              </v-layout>
            </template>
          </v-data-table>
        </div>
      </v-col>
    </v-layout>
  </div>
</template>

<style scoped>
.value-pointer {
  cursor: pointer;
}
</style>

<script>
import SelectActions from "./SelectActions.vue";
import FilterComp from "@/components/shared/Filter.vue";
import PagingComp from "@/components/shared/Paging.vue";
import { mapGetters } from "vuex";
import adminApi from "@/api/admin";
import utils from "@/utils/userType";

export default {
  props: ["selects", "has_more", "loading"],
  components: {
    SelectActions,
    FilterComp,
    PagingComp,
  },
  data() {
    return {
      account_promises: {},
      accounts: {},
      user_promises: {},
      users: {},
      selected_account: "",
      support_accounts: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: "Name / Select ID", value: "query_name_id" },
        { text: "Account", value: "account_name" },
        { text: "User", value: "user_name" },
        { text: "Select Date", value: "created" },
        { text: "Expiration Date", value: "expire_date" },
        { text: "Actions", value: "actions", sortable: false },
      ];
    },
    ...mapGetters(["user_details", "account_details"]),
  },
  mounted() {
    this.waitThenGetSupportAccounts();
  },
  methods: {
    is_supported_account(account_id) {
      return utils.is_supported_account(account_id, this.user_details);
    },

    is_admin() {
      return utils.is_admin(this.user_details);
    },

    getAccountDetails(account_id) {
      if (!this.account_promises[account_id]) {
        // cache the promises so we're not making requests for the same account
        this.account_promises[account_id] = adminApi
          .get_account(account_id)
          .then((response) => {
            this.accounts[account_id] = response.data.account;
            // TODO: there's likely a better way
            this.$forceUpdate(); // things will likely have already rendered
          })
          .catch((error) => {
            return error;
          });
      }

      return this.accounts[account_id] || { name: account_id };
    },

    getUserDetails(account_id, user_id) {
      /*
       * we must be an admin or the account must be a supported account
       * for us to fetch the user id's name so we don't get a forbidden
       * error in the UI... more details here:
       *
       * https://maxar.atlassian.net/browse/AEA-945
       */
      if (
        !this.user_promises[user_id] &&
        (this.is_admin() || this.is_supported_account(account_id))
      ) {
        // cache the promises so we're not making requests for the same user
        this.user_promises[user_id] = adminApi
          .get_user(account_id, user_id)
          .then((response) => {
            this.users[user_id] = response.data.user;
            // TODO: there's likely a better way
            this.$forceUpdate(); // things will likely have already rendered
          })
          .catch((error) => {
            return error;
          });
      }

      return this.users[user_id] || { name: user_id };
    },

    viewAccount(account_id) {
      this.$router.push({
        name: "account-details",
        params: { account_id: account_id },
      });
    },

    viewUser(account_id, user_id) {
      this.$router.push({
        name: "user-details",
        params: { account_id: account_id, user_id: user_id },
      });
    },

    applyFilters(start_date, end_date, filters, named_only) {
      // reset paging and push query params to router
      this.$refs.pagingComp.refreshPaging();
      this.$router
        .push({
          query: {
            start_date: start_date,
            end_date: end_date,
            filters: filters,
            named_only: named_only,
          },
        })
        .catch(() => {
          // catch redundant path error
        });
    },

    getPagingId() {
      let paging_id = "";
      if (this.selects.length > 0) {
        paging_id = this.selects.slice(-1)[0]["query_id"];
      }
      return paging_id;
    },

    waitThenGetSupportAccounts() {
      if (this.user_details.account_id) {
        if (
          ("support_accounts" in this.user_details &&
            this.user_details.support_accounts.length > 0) ||
          this.user_details.super_admin
        ) {
          adminApi
            .fetch_all_accounts()
            .then((response) => {
              this.support_accounts = response;
            })
            .catch((error) => {
              return error;
            });
        }
      } else {
        setTimeout(this.waitThenGetSupportAccounts, 200);
      }
    },

    viewSelect(select_id) {
      this.$router.push({
        name: "Select",
        query: {
          view: select_id,
        },
      });
    },
  },
};
</script>
<style lang="css" scoped>
.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>
